import { getApiProxyUnavailableMessage, isApiProxyUnavailableError } from '@app/api-client';
import { useRouterState } from '@tanstack/react-router';
import { LogOut } from 'lucide-react';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { fetchAuthSessionHint, logout, refresh } from '../features/auth/api';
import { LoginPage } from '../features/auth/pages/LoginPage';
import { useAuthStore } from '../features/auth/store';
import { fetchDemoStatus } from '../features/demo/api';
import { DemoBar } from '../features/demo/components/DemoBar';
import { useDemoStore } from '../features/demo/store/demo.store';
import { SiteSwitcher } from '../features/progression/components/SiteSwitcher';
import { useMySites } from '../features/progression/hooks/useProgression';
import { useSelectedPhaseStore } from '../features/progression/store/selected-phase.store';
import { useSiteSelectionStore } from '../features/progression/store/site-selection.store';
import {
  usePublicationPendingValidations,
  usePublicationRejectedSubmissions,
} from '../features/publication/hooks/usePublicationLists.js';
import { fetchSetupStatus } from '../features/setup/api';
import { SetupWizardPage } from '../features/setup/pages/SetupWizardPage';
import { roleLabel } from '../lib/role-labels';
import { NavItem } from './NavItem';
import { MENU_CONFIG, type PhaseCode, phaseOrderOf } from './menu.config';

interface Props {
  children: ReactNode;
}

/**
 * Phase de parcours de l'utilisateur sur le site sélectionné (liaison user ↔ site),
 * pour piloter la **visibilité** du menu — pas la phase agrégée du site seul.
 * `null` si aucun site sélectionné / affectation introuvable.
 */
function useMenuUserPhaseForSelectedSite(
  enabled: boolean,
  selectedSiteId: string | null,
): PhaseCode | null {
  const sites = useMySites(enabled);
  if (!sites.data || sites.data.length === 0 || !selectedSiteId) return null;
  const site = sites.data.find((s) => s.siteId === selectedSiteId);
  return (site?.userPhaseCode as PhaseCode | undefined) ?? null;
}

export function Layout({ children }: Props) {
  const { user, accessToken, setSession, clearSession } = useAuthStore();
  const setDemoStatus = useDemoStore((s) => s.setStatus);
  const [bootstrapping, setBootstrapping] = useState(true);
  const [requiresSetup, setRequiresSetup] = useState(false);
  const [backendUnavailableMessage, setBackendUnavailableMessage] = useState<string | null>(null);

  const isAuthenticated = !bootstrapping && !!accessToken && !!user;
  const sites = useMySites(isAuthenticated);
  const { selectedSiteId, setSelectedSiteId } = useSiteSelectionStore();

  const { data: pendingValidationList } = usePublicationPendingValidations({
    enabled: isAuthenticated,
  });
  const { data: rejectedSubmissionList } = usePublicationRejectedSubmissions({
    enabled: isAuthenticated,
  });

  const publicationBadgeCounts = useMemo(
    () => ({
      'publication-pending': pendingValidationList?.length ?? 0,
      'publication-rejected': rejectedSubmissionList?.length ?? 0,
    }),
    [pendingValidationList, rejectedSubmissionList],
  );

  // Doit être appelé inconditionnellement avant tout return anticipé (Règles des Hooks)
  const menuUserPhase = useMenuUserPhaseForSelectedSite(isAuthenticated, selectedSiteId);
  const userPhaseOrder = phaseOrderOf(menuUserPhase);

  /**
   * Phase sélectionnée dans la page Parcours. Fallback = phase utilisateur sur le site actif
   * pour éviter qu'aucune étoile ne s'affiche au premier chargement.
   */
  const storeSelectedPhase = useSelectedPhaseStore((s) => s.selectedPhaseCode);
  const selectedPhase: PhaseCode | null = storeSelectedPhase ?? menuUserPhase;

  const pathname = useRouterState({ select: (s) => s.location.pathname });
  /** Routes où la zone centrale utilise toute la largeur (grilles larges, carto…). */
  const mainFullWidth =
    pathname === '/parametres/groupes-et-processus' || pathname === '/cartographie';
  /** Cartographie : pas de scroll sur le main, la page remplit la hauteur (flux plein cadre). */
  const mainFillHeight = pathname === '/cartographie';

  useEffect(() => {
    let cancelled = false;
    async function bootstrap() {
      const demo = await fetchDemoStatus();
      if (cancelled) return;
      setDemoStatus(demo);
      try {
        const status = await fetchSetupStatus();
        if (cancelled) return;
        if (status.requiresSetup) {
          setRequiresSetup(true);
          setBootstrapping(false);
          return;
        }
      } catch (e) {
        if (!cancelled && isApiProxyUnavailableError(e)) {
          setBackendUnavailableMessage(
            getApiProxyUnavailableMessage(e) ??
              "Le serveur d'API n'est pas joignable depuis le proxy de développement. Lancez l'API Nest (ex. `pnpm dev` à la racine du dépôt) ou ajustez `VITE_API_PROXY_TARGET` dans `apps/web/.env`.",
          );
          setBootstrapping(false);
          return;
        }
        // ignore: even if status fails we try refresh
      }
      if (cancelled) return;
      if (accessToken) {
        setBootstrapping(false);
        return;
      }
      try {
        const hint = await fetchAuthSessionHint();
        if (cancelled) return;
        if (hint.refreshCookiePresent) {
          const res = await refresh();
          if (cancelled) return;
          setSession(res.accessToken, res.user);
        }
      } catch {
        // Pas de cookie refresh ou jeton expiré : vider la session périmée et afficher le login
        clearSession();
      } finally {
        if (!cancelled) setBootstrapping(false);
      }
    }
    void bootstrap();
    return () => {
      cancelled = true;
    };
  }, [accessToken, setSession, clearSession, setDemoStatus]);

  if (backendUnavailableMessage !== null) {
    return (
      <div className="flex min-h-[100dvh] flex-col items-center justify-center gap-6 bg-[radial-gradient(120%_80%_at_50%_-10%,rgba(239,68,68,0.12),transparent),linear-gradient(to_bottom,#ffffff,rgb(248,250,252)_45%)] px-6">
        <div className="max-w-lg space-y-4 text-center">
          <h1 className="text-xl font-semibold tracking-tight text-slate-900">API indisponible</h1>
          <p className="text-[0.95rem] leading-relaxed text-slate-600">
            {backendUnavailableMessage}
          </p>
          <button
            type="button"
            className="rounded-xl bg-emerald-600 px-5 py-2.5 text-sm font-medium text-white shadow-md shadow-emerald-900/15 transition-colors hover:bg-emerald-700"
            onClick={() => window.location.reload()}
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  if (bootstrapping) {
    return (
      <div className="flex min-h-[100dvh] flex-col items-center justify-center gap-5 bg-[radial-gradient(120%_80%_at_50%_-10%,rgba(16,185,129,0.18),transparent),linear-gradient(to_bottom,#ffffff,rgb(248,250,252)_45%)]">
        <div className="flex flex-col items-center gap-5" aria-busy="true" aria-live="polite">
          <span className="relative flex h-12 w-12" aria-hidden="true">
            <span className="absolute inset-0 rounded-full border-[3px] border-slate-200" />
            <span className="absolute inset-0 animate-spin rounded-full border-[3px] border-transparent border-t-emerald-500" />
          </span>
          <p className="text-[0.9rem] font-medium tracking-tight text-slate-600">
            Chargement de l’espace de travail…
          </p>
        </div>
      </div>
    );
  }

  if (requiresSetup) {
    return (
      <>
        <DemoBar />
        <SetupWizardPage onCompleted={() => setRequiresSetup(false)} />
      </>
    );
  }

  if (!user || !accessToken)
    return (
      <>
        <DemoBar />
        <LoginPage />
      </>
    );

  const topItems = MENU_CONFIG.filter((i) => (i.position ?? 'top') === 'top');
  const bottomItems = MENU_CONFIG.filter((i) => i.position === 'bottom');

  return (
    <div className="flex min-h-[100dvh] flex-col bg-slate-50 font-sans">
      <DemoBar />

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <aside className="flex w-72 flex-col border-r border-slate-200 bg-white">
          <div className="flex items-center gap-3 border-b border-slate-100 px-6 py-5">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-500 to-teal-700 text-lg font-semibold tracking-tighter text-white shadow-md shadow-teal-800/20">
              S
            </span>
            <div className="min-w-0">
              <h1 className="truncate font-bold tracking-tight text-slate-900">Square ISO</h1>
            </div>
          </div>

          <div className="border-b border-slate-100">
            {sites.data && (
              <SiteSwitcher
                sites={sites.data}
                selectedSiteId={selectedSiteId}
                onChange={setSelectedSiteId}
              />
            )}
          </div>

          <nav className="flex-1 space-y-1.5 overflow-y-auto px-3 py-4">
            {topItems.map((item) => (
              <NavItem
                key={item.id}
                item={item}
                userPhaseOrder={userPhaseOrder}
                selectedPhase={selectedPhase}
                badgeCounts={publicationBadgeCounts}
              />
            ))}
          </nav>

          <div className="border-t border-slate-200 p-4">
            <div className="mb-2 space-y-1.5">
              {bottomItems.map((item) => (
                <NavItem
                  key={item.id}
                  item={item}
                  userPhaseOrder={userPhaseOrder}
                  selectedPhase={selectedPhase}
                  badgeCounts={publicationBadgeCounts}
                />
              ))}
            </div>

            <div className="flex items-center justify-between rounded-xl bg-slate-50 px-3 py-3">
              <div className="flex min-w-0 flex-col">
                <p className="truncate text-sm font-semibold text-slate-900">
                  {user.firstName || user.lastName
                    ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim()
                    : user.alias}
                </p>
                <p className="truncate text-[0.75rem] font-medium text-slate-500">
                  {roleLabel(user.role)}
                </p>
              </div>
              <button
                type="button"
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-200 hover:text-slate-700"
                onClick={async () => {
                  await logout().catch(() => undefined);
                  clearSession();
                }}
                title="Déconnexion"
              >
                <LogOut className="h-4 w-4" />
              </button>
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main
          className={
            mainFillHeight
              ? 'flex min-h-0 flex-1 flex-col overflow-hidden'
              : 'flex-1 overflow-y-auto'
          }
        >
          <div
            className={
              mainFillHeight
                ? 'flex min-h-0 w-full max-w-none flex-1 flex-col px-6 py-4 lg:px-8'
                : mainFullWidth
                  ? 'w-full max-w-none px-6 py-10 lg:px-8'
                  : 'mx-auto max-w-5xl px-6 py-10 lg:px-8'
            }
          >
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
